import React, { useState } from 'react';
import { useClerk, useSignIn } from '@clerk/clerk-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../contexts/useLanguage';
import { normalizeAppRole, portalHomePathForRole } from '../../lib/portalRouting';
import { getClerkErrorMessage, isPasswordIncorrectError, isAccountNotFoundError } from './clerkAuthErrors';
import { ClerkSignInCardSkeleton } from './ClerkAuthCardSkeleton';
import { ClerkSecuredByFooter } from './ClerkSecuredByFooter';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { BorderBeam } from '../ui/border-beam';

type SignInMode = 'password' | 'email_code';

/** credentials = email/password or email + send code; first_factor_code = OTP after email_code first factor; second_factor = MFA email code */
type Phase = 'credentials' | 'first_factor_code' | 'second_factor';

function pickEmailCodeFirstFactor(
  signIn: { supportedFirstFactors: Array<{ strategy: string } & Record<string, unknown>> | null | undefined },
) {
  const factors = signIn.supportedFirstFactors;
  if (!factors?.length) return null;
  for (const f of factors) {
    if (f.strategy === 'email_code' && 'emailAddressId' in f && f.emailAddressId) {
      return f as { strategy: 'email_code'; emailAddressId: string };
    }
  }
  return null;
}

export interface CustomClerkSignInFormProps {
  signUpHref: string;
  /** Post-auth redirect when Clerk returns a path (default matches prior prebuilt `afterSignInUrl`). */
  afterSignInRedirect?: string;
  /** When set, "Sign up" uses this instead of navigating to `signUpHref` (e.g. slide-over tab). */
  onPressSignUp?: () => void;
  /**
   * Called after `setActive` succeeds, before navigation.
   * Return a route path to navigate there; otherwise `afterSignInRedirect` is used.
   */
  onAfterSignInComplete?: () => string | void;
}

export function CustomClerkSignInForm({
  signUpHref,
  afterSignInRedirect = '/client',
  onPressSignUp,
  onAfterSignInComplete,
}: CustomClerkSignInFormProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isLoaded, signIn } = useSignIn();
  const clerk = useClerk();
  const { setActive } = clerk;

  const [signInMode, setSignInMode] = useState<SignInMode>('password');
  const [phase, setPhase] = useState<Phase>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSignUpHint, setShowSignUpHint] = useState(false);

  /** Manager/admin → /manager, staff/employee → /staff; otherwise caller redirect. */
  const goAfterAuth = async () => {
    const custom = onAfterSignInComplete?.();
    // Session user can lag one tick after setActive; reload when available.
    let roleMeta: unknown = clerk.user?.publicMetadata?.role ?? clerk.session?.user?.publicMetadata?.role;
    if (clerk.user) {
      try {
        const reloaded = await clerk.user.reload();
        roleMeta = reloaded.publicMetadata?.role ?? roleMeta;
      } catch {
        /* keep roleMeta from session */
      }
    }
    const role = normalizeAppRole(roleMeta);
    if (role === 'manager' || role === 'staff') {
      navigate({ to: portalHomePathForRole(role) });
      return;
    }
    const target =
      typeof custom === 'string' && custom.length > 0 ? custom : afterSignInRedirect;
    navigate({ to: target });
  };

  const resetToCredentials = () => {
    setPhase('credentials');
    setCode('');
    setError(null);
  };

  const handleSendEmailCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    const id = email.trim();
    if (!id) {
      setError(t('clerkAuth.emailRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const attempt = await signIn.create({
        identifier: id,
      });

      if (attempt.status === 'complete' && attempt.createdSessionId) {
        await setActive({ session: attempt.createdSessionId });
        await goAfterAuth();
        return;
      }

      if (attempt.status !== 'needs_first_factor') {
        setError(t('clerkAuth.errorSignInIncomplete'));
        return;
      }

      const emailFactor = pickEmailCodeFirstFactor(signIn);
      if (!emailFactor) {
        setError(t('clerkAuth.errorEmailCodeUnsupported'));
        return;
      }

      await signIn.prepareFirstFactor({
        strategy: 'email_code',
        emailAddressId: emailFactor.emailAddressId,
      });
      setPhase('first_factor_code');
    } catch (err) {
      setError(getClerkErrorMessage(err));
      if (isAccountNotFoundError(err)) setShowSignUpHint(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFirstFactorCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'email_code',
        code,
      });

      if (result.status === 'complete' && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        await goAfterAuth();
        return;
      }

      if (result.status === 'needs_second_factor') {
        const secondFactors = result.supportedSecondFactors ?? signIn.supportedSecondFactors;
        const emailCodeFactor = secondFactors?.find((f) => f.strategy === 'email_code');
        if (
          emailCodeFactor &&
          emailCodeFactor.strategy === 'email_code' &&
          'emailAddressId' in emailCodeFactor
        ) {
          await signIn.prepareSecondFactor({
            strategy: 'email_code',
            emailAddressId: emailCodeFactor.emailAddressId,
          });
          setCode('');
          setPhase('second_factor');
          return;
        }
        setError(t('clerkAuth.errorSecondFactorUnsupported'));
        return;
      }

      setError(t('clerkAuth.errorInvalidOtp'));
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendFirstFactor = async () => {
    if (!isLoaded || !signIn) return;
    const emailFactor = pickEmailCodeFirstFactor(signIn);
    if (!emailFactor) {
      setError(t('clerkAuth.errorEmailCodeUnsupported'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await signIn.prepareFirstFactor({
        strategy: 'email_code',
        emailAddressId: emailFactor.emailAddressId,
      });
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    setSubmitting(true);
    setError(null);
    try {
      const attempt = await signIn.create({
        identifier: email,
        password,
      });

      if (attempt.status === 'complete' && attempt.createdSessionId) {
        await setActive({ session: attempt.createdSessionId });
        await goAfterAuth();
        return;
      }

      if (attempt.status === 'needs_second_factor') {
        const emailCodeFactor = attempt.supportedSecondFactors?.find((f) => f.strategy === 'email_code');
        if (emailCodeFactor && emailCodeFactor.strategy === 'email_code' && 'emailAddressId' in emailCodeFactor) {
          await signIn.prepareSecondFactor({
            strategy: 'email_code',
            emailAddressId: emailCodeFactor.emailAddressId,
          });
          setCode('');
          setPhase('second_factor');
          return;
        }
        setError(t('clerkAuth.errorSecondFactorUnsupported'));
        return;
      }

      setError(t('clerkAuth.errorSignInIncomplete'));
    } catch (err) {
      setError(getClerkErrorMessage(err));
      if (isAccountNotFoundError(err)) setShowSignUpHint(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSecondFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await signIn.attemptSecondFactor({
        strategy: 'email_code',
        code,
      });

      if (result.status === 'complete' && result.createdSessionId) {
        await setActive({ session: result.createdSessionId });
        await goAfterAuth();
        return;
      }

      setError(t('clerkAuth.errorInvalidOtp'));
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendSecondFactor = async () => {
    if (!isLoaded || !signIn) return;
    const factors = signIn.supportedSecondFactors;
    const emailCodeFactor = factors?.find((f) => f.strategy === 'email_code');
    if (
      !emailCodeFactor ||
      emailCodeFactor.strategy !== 'email_code' ||
      !('emailAddressId' in emailCodeFactor)
    ) {
      setError(t('clerkAuth.errorSecondFactorUnsupported'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await signIn.prepareSecondFactor({
        strategy: 'email_code',
        emailAddressId: emailCodeFactor.emailAddressId,
      });
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isLoaded) {
    return <ClerkSignInCardSkeleton />;
  }

  return (
    <div className="w-full max-w-[420px]">
      <Card className="relative overflow-hidden rounded-2xl border-zinc-200/90 bg-white/95 shadow-xl backdrop-blur-sm">
        <div className="relative z-10">
          {phase === 'credentials' ? (
            <form onSubmit={signInMode === 'password' ? handleCredentials : handleSendEmailCode}>
              <CardHeader className="pb-2">
                <CardTitle className="text-xl text-zinc-900">{t('clerkAuth.signInTitle')}</CardTitle>
                <CardDescription className="text-zinc-600">
                  {t('clerkAuth.signInDescription')}
                </CardDescription>
                <p className="pt-2 text-sm text-zinc-500">
                  {t('clerkAuth.noAccount')}{' '}
                  {onPressSignUp ? (
                    <button
                      type="button"
                      onClick={onPressSignUp}
                      className="font-medium text-emerald-700 hover:underline"
                    >
                      {t('clerkAuth.signUpLink')}
                    </button>
                  ) : (
                    <Link to={signUpHref} className="font-medium text-emerald-700 hover:underline">
                      {t('clerkAuth.signUpLink')}
                    </Link>
                  )}
                </p>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                      signInMode === 'password'
                        ? 'border-emerald-600 bg-emerald-50 text-emerald-900'
                        : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
                    }`}
                    onClick={() => {
                      setSignInMode('password');
                      setError(null);
                      setShowSignUpHint(false);
                    }}
                  >
                    {t('clerkAuth.signInWithPassword')}
                  </button>
                  <button
                    type="button"
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                      signInMode === 'email_code'
                        ? 'border-emerald-600 bg-emerald-50 text-emerald-900'
                        : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
                    }`}
                    onClick={() => {
                      setSignInMode('email_code');
                      setError(null);
                      setShowSignUpHint(false);
                    }}
                  >
                    {t('clerkAuth.signInWithEmailCode')}
                  </button>
                </div>
              </CardHeader>
              <CardContent className="grid gap-4 pb-2">
                <div className="grid gap-2">
                  <Label htmlFor="clerk-signin-email">{t('clerkAuth.email')}</Label>
                  <Input
                    id="clerk-signin-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(ev) => { setEmail(ev.target.value); setShowSignUpHint(false); }}
                    placeholder="name@example.com"
                    className="border-zinc-300 focus-visible:ring-emerald-500"
                  />
                </div>
                {signInMode === 'password' ? (
                  <div className="grid gap-2">
                    <Label htmlFor="clerk-signin-password">{t('clerkAuth.password')}</Label>
                    <Input
                      id="clerk-signin-password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(ev) => setPassword(ev.target.value)}
                      className="border-zinc-300 focus-visible:ring-emerald-500"
                    />
                    <div className="flex justify-end">
                      <Link
                        to="/forgot-password"
                        className="text-sm font-medium text-emerald-800 hover:underline"
                      >
                        {t('clerkAuth.forgotPasswordLink')}
                      </Link>
                    </div>
                  </div>
                ) : null}
                {error ? (
                  <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                    {error}
                  </p>
                ) : null}
                {showSignUpHint ? (
                  <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-900">
                    <span className="mt-px shrink-0 text-base leading-none">ℹ️</span>
                    <div className="min-w-0">
                      <p className="leading-snug">{t('clerkAuth.accountNotFound')}</p>
                      {onPressSignUp ? (
                        <button
                          type="button"
                          onClick={onPressSignUp}
                          className="mt-1.5 inline-block font-semibold text-blue-800 hover:underline"
                        >
                          {t('clerkAuth.accountNotFoundCta')}
                        </button>
                      ) : (
                        <Link
                          to={signUpHref}
                          className="mt-1.5 inline-block font-semibold text-blue-800 hover:underline"
                        >
                          {t('clerkAuth.accountNotFoundCta')}
                        </Link>
                      )}
                    </div>
                  </div>
                ) : null}
              </CardContent>
              <CardFooter className="flex-col gap-3 border-t border-zinc-100 pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-emerald-700 text-white hover:bg-emerald-800"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {signInMode === 'password' ? t('clerkAuth.signInSubmit') : t('clerkAuth.sendVerificationCode')}
                </Button>
              </CardFooter>
            </form>
          ) : phase === 'first_factor_code' ? (
            <form onSubmit={handleFirstFactorCode}>
              <CardHeader className="pb-2">
                <CardTitle className="text-xl text-zinc-900">{t('clerkAuth.verificationTitle')}</CardTitle>
                <CardDescription className="text-zinc-600">{t('clerkAuth.verificationHint')}</CardDescription>
                <p className="pt-1 text-sm text-zinc-500">{email}</p>
              </CardHeader>
              <CardContent className="grid gap-4 pb-2">
                <div className="grid gap-2">
                  <Label htmlFor="clerk-signin-code-first">{t('clerkAuth.codeLabel')}</Label>
                  <Input
                    id="clerk-signin-code-first"
                    name="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(ev) => setCode(ev.target.value)}
                    className="border-zinc-300 text-center text-lg tracking-[0.2em] focus-visible:ring-emerald-500"
                  />
                </div>
                {error ? (
                  <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                    {error}
                  </p>
                ) : null}
              </CardContent>
              <CardFooter className="flex-col gap-2 border-t border-zinc-100 pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-emerald-700 text-white hover:bg-emerald-800"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {t('clerkAuth.verifySubmit')}
                </Button>
                <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-between">
                  <button
                    type="button"
                    className="text-sm text-emerald-700 hover:underline disabled:opacity-50"
                    disabled={submitting}
                    onClick={handleResendFirstFactor}
                  >
                    {t('clerkAuth.resendCode')}
                  </button>
                  <button
                    type="button"
                    className="text-center text-sm text-zinc-600 hover:underline"
                    onClick={resetToCredentials}
                  >
                    {t('clerkAuth.backToCredentials')}
                  </button>
                </div>
              </CardFooter>
            </form>
          ) : (
            <form onSubmit={handleSecondFactor}>
              <CardHeader className="pb-2">
                <CardTitle className="text-xl text-zinc-900">{t('clerkAuth.clientTrustTitle')}</CardTitle>
                <CardDescription className="text-zinc-600">
                  {t('clerkAuth.signInVerifyDescription')}
                </CardDescription>
                <p className="pt-1 text-sm text-zinc-600">{t('clerkAuth.clientTrustHint')}</p>
              </CardHeader>
              <CardContent className="grid gap-4 pb-2">
                <div className="grid gap-2">
                  <Label htmlFor="clerk-signin-code">{t('clerkAuth.codeLabel')}</Label>
                  <Input
                    id="clerk-signin-code"
                    name="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(ev) => setCode(ev.target.value)}
                    className="border-zinc-300 text-center text-lg tracking-[0.2em] focus-visible:ring-emerald-500"
                  />
                </div>
                {error ? (
                  <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                    {error}
                  </p>
                ) : null}
              </CardContent>
              <CardFooter className="flex-col gap-2 border-t border-zinc-100 pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-emerald-700 text-white hover:bg-emerald-800"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {t('clerkAuth.verifySubmit')}
                </Button>
                <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-between">
                  <button
                    type="button"
                    className="text-sm text-emerald-700 hover:underline disabled:opacity-50"
                    disabled={submitting}
                    onClick={handleResendSecondFactor}
                  >
                    {t('clerkAuth.resendCode')}
                  </button>
                  <button
                    type="button"
                    className="text-center text-sm text-emerald-800/90 hover:underline"
                    onClick={resetToCredentials}
                  >
                    {t('clerkAuth.backToCredentials')}
                  </button>
                </div>
              </CardFooter>
            </form>
          )}
        </div>
        <BorderBeam duration={8} size={100} borderWidth={2} colorFrom="#ffaa40" colorTo="#9c40ff" />
      </Card>

      <ClerkSecuredByFooter />
    </div>
  );
}
